import type { DerivationFilter } from '@mathflat/domain/@entities/(Content)/Filter/type'
import type { Entity as WorkbookEntity } from '@mathflat/domain/@entities/(Content)/Workbook/dto'
import type { SchoolType } from '@mathflat/domain/@entities/SchoolSystem/schema'
import { makeObservable, observable } from 'mobx'
import { EMPTY_NUMBER_ID } from 'src/constant/common'

import { DTO } from '../DTO'
import type { Worksheet } from './Worksheet'

export class MakeWorksheetFilterOption extends DTO {
  // 공통
  type: res.WorksheetDetail['filter']['type'] = 'CONCEPT' // TODO - fix 학습지만들기 1단계가 어떤건지 : 'CONCEPT' - 단원유형 PUBLIC_WORKBOOK - 시중교재 PRACTICE_TEST - 수능 모의고사 SCHOOL_TEST - 학교별기출 SCHOOL_TEST_PAPER - 기출업로드
  filterId = '' // 필터 고유 아이디
  practiceTest: common.PracticeTestOption = 'INCLUDE' // 모의고사 문제 포함여부 - include: 문제 포함, only: 문제만, exclude: 문제 제외
  onlyAutoScorable = false // 자동채점 가능여부
  excludePrevious = false // 기존 출제문제 제외여부
  previousExclusionScope?: common.PreviousExclusionScope // 기존 출제 문제 제외 기준
  studentIds?: res.Student['id'][] // 제외 기준이 STUDENT_ASSIGNED일 때 대상 학생 id 목록
  problemFilterType: common.ProblemFilterType = 'ALL'
  excludeOOC = false // 교육과정 외 유형 제외
  includeMyDbProblem = true // 본인 업로드(나의 DB) 문제 포함 여부
  studentId = ''

  problemList: res.MakeWorksheetProblem[] | null = null // 문제 아이디 리스트 1단계 - null 복제후 수정, 수정은 기존 학습지 문제 아이디 리스트를 가지고 있음.

  title = '' // 학습지 3단계 제목
  grade: common.GradeSubject = '1' // 학년
  semester: common.Semester | null = null // 학기
  schoolType: res.Student['schoolType'] | null = null // 학교급 정보
  revision: common.Revision = 'CURRICULUM_15' // 개정
  worksheetRange: string | null = null // 학습지 범위

  selectedConceptIdList: number[] = [] // 선택한 유형별 개념
  selectedLittleChapterIdList: number[] = [] // 선택한 소단원별 개념

  // 단원 유형별 옵션
  conceptIdList: number[] = [] // 유형 선택 아이디리스트
  excludedTopicIds: number[] = [] // 제외할 주제(유형) id list
  excludedSubTopicIds: number[] = [] // 제외할 세부(유형) id list
  problemCount = 50 // 문제 수
  level: common.ProblemLevel = 3 // 문제 난이도
  levelWeight: [number, number, number, number, number] = [100, 0, 0, 0, 0]
  equalityLevel: common.MakeWorksheetEqualityLevel = null // 균등배분시 기준 - 선택 안하면 null
  minRate = 0 // 정답률 - 최대
  maxRate = 100 // 정답률 - 최대

  // 시중교재
  problemOption: common.MakeWorksheetFilterProblemOption = 'ALL' // 문제 옵션 SIMILAR - 유사문제만 TWIN - 쌍둥이문제만 NULL - 둘다 포함
  pairX: 0 | 1 | 2 | 3 = 1 // 쌍둥이 문제 수
  similarX: 0 | 1 | 2 | 3 = 1 // 유사 문제 수
  workbookId: number = EMPTY_NUMBER_ID // 시중교재 아이디
  similarLevel: common.MakeWorksheetSimilarLevel = 'AS_IS' // 유사문제 난이도 EASIER - 더 쉽게 AS_IS - 그대로 HARDER - 더 어렵게
  limitProblemCountPerConcept: req.CreateFilterByPublicWorkbook['limitProblemCountPerConcept'] =
    null // 유형별 최대 문제수 제한
  workbookProblemIdList: req.CreateFilterByPublicWorkbook['workbookProblemIdList'] = [] // 선택한 문제 아이디
  // 시중교재로 만들 학습지일 경우 존재
  originWorkbook: WorkbookEntity.CustomWorkbook['originWorkbook'] = null
  // 수능
  practiceProblemIdList: number[] = [] // 학습지 선택 아이디리스트
  curriculumIdList: number[] = []
  schoolGradeType: 1 | 2 | 3 = 1 // 학년 (고1, 고2, 고3)
  year: number[] = [] // 대상 년도들
  department: ('LIBERAL' | 'SCIENCE')[] = [] // 문이과 - 전체면 null
  month: number[] = [] // 대상 월들
  subjectList: number[] = [] // 과목들
  scoreList: (2 | 3 | 4)[] = [2, 3, 4] // 배점 - 전체면 null

  // 그외
  academyId = '' // 기관 아이디
  timeToLive = 0 // ttl 시간단위
  source: string | undefined = undefined // origin worksheet type (보내는대로 오는 값)

  // WEAK_PERIOD
  startDate = ''
  endDate = ''

  // SCHOOL_TEST
  schoolCodes: string[] = []
  schoolTestGrade = undefined
  schoolTestSchoolType: Omit<SchoolType, 'ELEMENTARY'> | null = null
  schoolTestSemester = undefined
  schoolTestYears: number[] = []
  schoolTestType = null // SCHOOL_TEST_PAPER 도 사용

  // SCHOOL_TEST_PAPER
  // CUSTOM_PAPER
  paperId = EMPTY_NUMBER_ID
  paperTitle = ''
  selectedPageIndexes: number[] = []
  schoolName = ''
  schoolTestYear = EMPTY_NUMBER_ID
  trieKey = ''

  // ONE_CLICK
  problemSource: {
    type: common.ProblemSourceType
    filterType: common.ProblemSourceFilterType
    filterTypeOption: {
      filterCount: common.ProblemSourceFilterCount
    }
  } = {
    type: 'WORKSHEET',
    filterType: 'RECENTLY_SCORED',
    filterTypeOption: {
      filterCount: 5,
    },
  }
  problemRequestType: common.ProblemRequestType = 'SIMILAR'
  levelType: DerivationFilter.LevelType['levelType'] = 'NORMAL'

  // MERGED_WORKSHEET
  mergedWorksheetIds: Worksheet['id'][] = []
  problemSortType: common.WorksheetMergeProblemSortType = 'BY_CONCEPT'

  // MY_DB_ORIGINAL / MY_DB_SIMILAR
  myDbProblemDetailIds: number[] = []
  myDbProblemDetails: res.MyDbProblemDetail[] | null = null

  constructor(data?: Partial<MakeWorksheetFilterOption>) {
    super()

    if (data) {
      Object.assign(this, {
        ...data,
        excludedTopicIds: data.excludedTopicIds ?? [],
        excludedSubTopicIds: data.excludedSubTopicIds ?? [],
      })
    }

    makeObservable(this, {
      filterId: observable,
      practiceTest: observable,
      onlyAutoScorable: observable,
      excludePrevious: observable,
      previousExclusionScope: observable,
      studentIds: observable,
      problemFilterType: observable,
      excludeOOC: observable,
      includeMyDbProblem: observable,

      conceptIdList: observable,
      excludedTopicIds: observable,
      excludedSubTopicIds: observable,
      problemCount: observable,
      level: observable,
      levelWeight: observable,
      equalityLevel: observable,
      minRate: observable,
      maxRate: observable,

      problemOption: observable,
      pairX: observable,
      similarX: observable,
      similarLevel: observable,

      practiceProblemIdList: observable,
      curriculumIdList: observable,
      schoolGradeType: observable,
      year: observable,
      month: observable,
      department: observable,
      subjectList: observable,
      scoreList: observable,
      revision: observable,

      selectedConceptIdList: observable,
      selectedLittleChapterIdList: observable,

      startDate: observable,
      endDate: observable,
      source: observable,

      paperId: observable,
      paperTitle: observable,
      selectedPageIndexes: observable,
      schoolName: observable,
      schoolTestYear: observable,
      schoolTestType: observable,
      trieKey: observable,
      originWorkbook: observable,
      myDbProblemDetailIds: observable,
    })
  }

  get filterIdParams() {
    switch (this.type) {
      case 'PRACTICE_TEST':
        return this.getPracticeFilterParams()
      case 'PUBLIC_WORKBOOK':
        return this.getPublicWorkbookFilterParams()
      case 'WEAK_PERIOD':
        return this.getWeakPeriodFilterParams()
      case 'SCHOOL_TEST':
        return this.getSchoolTestFilterParams()
      case 'SCHOOL_TEST_PAPER':
        return this.getSchoolTestPaperFilterParams()
      case 'CUSTOM_PAPER':
        return this.getCustomPaperFilterParams()
      case 'ONE_CLICK':
        return this.getOneClickFilterParams()
      case 'MERGED_WORKSHEET':
        return this.getMergedWorksheetFilterParams()
      case 'MY_DB_ORIGINAL':
        return this.getMyDbOriginalFilterParams()
      case 'MY_DB_SIMILAR':
        return this.getMyDbSimilarFilterParams()
      default:
        // 'CONCEPT','WEAK_CONCEPT','WEAK_WORKSHEET'
        return this.getConceptFilterParams()
    }
  }

  // 기존 출제 문제 제외 조건은 excludePrevious + scope 가 함께 설정됐을 때만, studentIds 는 STUDENT_ASSIGNED 일 때만 전달한다
  private get previousExclusionParams(): Pick<
    req.CreateFilterByChapter,
    'previousExclusionScope' | 'studentIds'
  > {
    if (!this.excludePrevious || !this.previousExclusionScope) return {}
    return {
      previousExclusionScope: this.previousExclusionScope,
      ...(this.previousExclusionScope === 'STUDENT_ASSIGNED' && this.studentIds?.length
        ? { studentIds: this.studentIds }
        : {}),
    }
  }

  private getConceptFilterParams(): Omit<req.CreateFilterByChapter, 'problemList'> & {
    title: string
  } {
    return {
      filterId: this.filterId,
      conceptIdList: this.conceptIdList,
      excludedTopicIds: this.excludedTopicIds,
      excludedSubTopicIds: this.excludedSubTopicIds,
      problemCount: this.problemCount,
      level: this.level,
      levelWeight: this.levelWeight,
      problemFilterType: this.problemFilterType,
      practiceTest: this.practiceTest,
      onlyAutoScorable: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      excludeOOC: this.excludeOOC,
      includeMyDbProblem: this.includeMyDbProblem,
      equalityLevel: this.equalityLevel,
      minRate: this.minRate,
      maxRate: this.maxRate,
      title: this.title,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      ...this.previousExclusionParams,
    }
  }

  private getPublicWorkbookFilterParams(): Omit<
    req.CreateFilterByPublicWorkbook,
    'problemList' | 'exampleProblemIds'
  > {
    return {
      filterId: this.filterId,
      workbookId: this.workbookId,
      problemFilterType: this.problemFilterType,
      workbookProblemIdList: this.workbookProblemIdList,
      practiceTest: this.practiceTest,
      onlyAutoScorable: this.onlyAutoScorable,
      excludePrevious: this.excludePrevious,
      problemOption: this.problemOption,
      pairX: this.pairX,
      similarX: this.similarX,
      excludeOOC: this.excludeOOC,
      similarLevel: this.similarLevel,
      limitProblemCountPerConcept: this.limitProblemCountPerConcept,
      title: this.title,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      ...this.previousExclusionParams,
    }
  }

  private getPracticeFilterParams(): Omit<req.CreateFilterByPracticeTest, 'problemList'> & {
    title: string
  } {
    return {
      filterId: this.filterId,
      practiceProblemIdList: this.practiceProblemIdList,
      curriculumIdList: this.curriculumIdList,
      scoreList: this.scoreList,
      problemFilterType: this.problemFilterType,
      onlyAutoScorable: this.onlyAutoScorable,
      excludeOOC: this.excludeOOC,
      title: this.title,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
    }
  }

  private getWeakPeriodFilterParams(): Omit<req.CreateFilterByWeakPeriod, 'problemList'> {
    return { ...this.getConceptFilterParams(), startDate: this.startDate, endDate: this.endDate }
  }

  private getSchoolTestFilterParams(): Omit<req.CreateFilterBySchoolTest, 'problemList'> {
    return {
      conceptIdList: this.conceptIdList,
      excludeOOC: this.excludeOOC,
      excludePrevious: this.excludePrevious,
      filterId: this.filterId,
      limitProblemCountPerConcept: this.limitProblemCountPerConcept,
      onlyAutoScorable: this.onlyAutoScorable,
      pairX: this.pairX,
      practiceTest: this.practiceTest,
      problemFilterType: this.problemFilterType,
      title: this.title,
      similarX: this.similarX,
      similarLevel: this.similarLevel,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      schoolCodes: this.schoolCodes,
      schoolTestGrade: this.schoolTestGrade,
      schoolTestSchoolType: this.schoolTestSchoolType,
      schoolTestSemester: this.schoolTestSemester,
      schoolTestType: this.schoolTestType,
      schoolTestYears: this.schoolTestYears,
      ...this.previousExclusionParams,
    }
  }

  private getCustomPaperFilterParams(): Omit<req.CreateFilterByCustomPaper, 'problemList'> {
    return {
      filterId: this.filterId,
      paperId: this.paperId,
      selectedPageIndexes: this.selectedPageIndexes,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
    }
  }
  private getSchoolTestPaperFilterParams(): Omit<req.CreateFilterBySchoolTestPaper, 'problemList'> {
    return {
      filterId: this.filterId,
      paperId: this.paperId,
      selectedPageIndexes: this.selectedPageIndexes,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
    }
  }

  private getOneClickFilterParams(): Omit<req.CreateFilterByOneClick, 'problemList'> {
    return {
      studentId: this.studentId,
      filterId: this.filterId,
      conceptIdList: this.conceptIdList,
      selectedConceptIdList: this.selectedConceptIdList,
      selectedLittleChapterIdList: this.selectedLittleChapterIdList,
      problemSource: this.problemSource,
      problemRequestType: this.problemRequestType,
      onlyAutoScorable: this.onlyAutoScorable,
      excludeOOC: this.excludeOOC,
      problemFilterType: this.problemFilterType,
      excludePrevious: this.excludePrevious,
      problemCount: this.problemCount,
      levelType: this.levelType,
      practiceTest: this.practiceTest,
      title: this.title,
    }
  }

  private getMergedWorksheetFilterParams(): Omit<
    req.CreateFilterByMerge,
    | 'problems'
    | 'excludeOOC'
    | 'problemList'
    | 'selectedConceptIdList'
    | 'selectedLittleChapterIdList'
    | 'problemFilterType'
  > & {
    filterId: string
  } {
    return {
      filterId: this.filterId,
      mergedWorksheetIds: this.mergedWorksheetIds,
      problemSortType: this.problemSortType,
      onlyAutoScorable: this.onlyAutoScorable,
      title: this.title,
    }
  }

  private getMyDbOriginalFilterParams(): Omit<req.CreateFilterByMyDbOriginal, 'problemList'> {
    return {
      type: 'MY_DB_ORIGINAL',
      filterId: this.filterId,
      title: this.title,
      myDbProblemDetailIds: this.myDbProblemDetailIds,
    }
  }

  private getMyDbSimilarFilterParams(): Omit<req.CreateFilterByMyDbSimilar, 'problemList'> {
    return {
      type: 'MY_DB_SIMILAR',
      filterId: this.filterId,
      title: this.title,
      similarX: this.similarX,
      similarLevel: this.similarLevel,
      myDbProblemDetailIds: this.myDbProblemDetailIds,
    }
  }
}
